import { useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Icon,
  Spinner,
  Tag,
  Text,
} from '@nimbus-ds/components';
import { ErrorState, Input, Select } from '@tiendanube/components';
import { LockIcon } from '@tiendanube/icons';
import { useDesktopMenuContext } from 'App/components/Menu/DesktopMenuProvider';
import useTopBar from 'App/components/Topbar/useTopBar';
import { useToastStatus } from 'commons/hooks';
import { useGetCountry } from 'domains/Auth/hooks';
import { PaymentStatusAlertFailed } from 'domains/Billing/Checkout/components';
import {
  useAfterPaymentRedirection,
  useCreditCardCheckout,
  usePayorderToPay,
} from 'domains/Billing/Checkout/hooks';
import { trackingBillingPayCreditCardClick } from 'domains/Billing/Checkout/tracking';
import useTranslationBilling from 'domains/Billing/useTranslationBilling';
import CreditCardDataCardSkeleton from './Skeleton';
import {
  getIdentificationNumberPlaceholder,
  handleCardNumber,
  handleExpirationDate,
  handleIdentificationNumber,
  handleIndentificationType,
} from './utils';

interface CreditCardDataCardProps {
  readonly includeTransactionFee: boolean;
}

function CreditCardDataCard({
  includeTransactionFee,
}: CreditCardDataCardProps) {
  const t = useTranslationBilling(
    'checkout.payWithCreditCard.creditCardDataCard',
  );
  const { onCompletedPayment } = useAfterPaymentRedirection();
  const { isShowMenu, showMenu } = useTopBar();
  const { isShowMenu: isShowAsideMenu, showMenu: showAsideMenu } =
    useDesktopMenuContext();
  const country = useGetCountry();

  const { recurrentPaymentCompatible, mainConcept } = usePayorderToPay();

  const {
    errors,
    formValues,
    handleOnSubmit: executeForm,
    setFieldValue,
    isLoading,
    setupStatusObject: { isLoading: setupIsLoading, isError: setupIsError },
    cleanStatus,
    status,
    creditCardPaymentData,
  } = useCreditCardCheckout(includeTransactionFee);

  const handleOnSubmit = useCallback(() => {
    trackingBillingPayCreditCardClick(mainConcept);
    executeForm();
  }, [executeForm, mainConcept]);

  const onSuccess = useCallback(() => {
    onCompletedPayment();
    if (!isShowMenu || !isShowAsideMenu) {
      showMenu();
      showAsideMenu();
    }
  }, [
    onCompletedPayment,
    isShowMenu,
    isShowAsideMenu,
    showMenu,
    showAsideMenu,
  ]);

  const onError = useCallback(() => {
    cleanStatus();
  }, [cleanStatus]);

  useToastStatus({
    status,
    error: t('error'),
    success: t('success'),
    onSuccess,
    onError,
  });

  const getInputAttributes = (field: string, placeholder?: string) => {
    const error = errors[field];
    const helpText = error && error !== '' ? t(error) : '';
    const appearance =
      helpText !== '' ? ('validation_error' as const) : undefined;

    return {
      label: t(`form.${field}.label`),
      name: field,
      onChange: ({ value }) => setFieldValue(field, value),
      value: formValues[field],
      helpText,
      appearance,
      placeholder,
    };
  };

  if (setupIsError) return <ErrorState title={t('setupError')} />;

  /**
   * The loaded form is always present but hidden when mercado pago is loading.
   * This is because the setup for mercado pago needs the form to be present at all
   * times to properly mount.
   *  */
  return (
    <>
      {creditCardPaymentData?.successfulExecution === false && (
        <PaymentStatusAlertFailed
          payment={creditCardPaymentData.payment}
          errorCode={creditCardPaymentData.errorCode}
          isExternalContext={false}
          isFromRecurrentPayment={false}
          isFromCreditCardForm
        />
      )}
      {setupIsLoading && <CreditCardDataCardSkeleton />}
      <form id="form-checkout" hidden={setupIsLoading}>
        <Box display="flex" flexDirection="column" gap="2">
          <Card>
            <Card.Header title={t('title')} />
            <Tag appearance="neutral">
              <Icon source={<LockIcon size="small" />} /> {t('safetyTag')}
            </Tag>
            <Card.Body>
              <Box display="flex" flexDirection="column" gap="4">
                <Input
                  {...getInputAttributes('cardNumber', '1234 5678 9012 3456')}
                  onChange={({ value }) =>
                    handleCardNumber(value, setFieldValue)
                  }
                />
                <Input {...getInputAttributes('cardholderName')} />
                <Box display="flex" flexDirection="row" gap="4">
                  <Box display="flex" flexDirection="column" flexGrow="1">
                    <Input
                      {...getInputAttributes('expirationDate', 'mm/aa')}
                      onChange={({ value }) =>
                        handleExpirationDate(value, setFieldValue)
                      }
                    />
                  </Box>
                  <Box display="flex" flexDirection="column" flexGrow="1">
                    <Input {...getInputAttributes('securityCode', '123')} />
                  </Box>
                </Box>
                {country !== 'MX' && (
                  <Box display="flex" flexDirection="row" gap="4">
                    <Box display="flex" flexDirection="column" flex="1 1 35%">
                      <Select
                        {...getInputAttributes('identificationType')}
                        options={[]}
                        onChange={({ value }) =>
                          handleIndentificationType(value, setFieldValue)
                        }
                      />
                    </Box>
                    <Box display="flex" flexDirection="column" flex="1 1 65%">
                      <Input
                        {...getInputAttributes(
                          'identificationNumberParsed',
                          getIdentificationNumberPlaceholder(
                            country,
                            formValues.identificationType,
                          ),
                        )}
                        onChange={({ value }) =>
                          handleIdentificationNumber(
                            value,
                            country,
                            formValues.identificationType,
                            setFieldValue,
                          )
                        }
                      />
                    </Box>
                  </Box>
                )}
                <Box display="flex" flexDirection="column" gap="2">
                  <Input {...getInputAttributes('cardholderEmail')} />
                  <Text fontSize="caption">
                    {t('form.cardholderEmail.suggestion')}
                  </Text>
                </Box>
                {recurrentPaymentCompatible && (
                  <Alert>{t('recurrencyAlert')}</Alert>
                )}

                <Box
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                  gap="4"
                >
                  <Text>{t('processedBy')}</Text>
                </Box>
              </Box>
            </Card.Body>
          </Card>

          <Box display="flex" flexDirection="row" justifyContent="flex-end">
            <Button
              type="submit"
              appearance="primary"
              onClick={handleOnSubmit}
              disabled={isLoading}
            >
              {recurrentPaymentCompatible
                ? t('payButtonWithRp')
                : t('payButton')}
              {isLoading && <Spinner size="small" />}
            </Button>
          </Box>
        </Box>

        <input
          hidden
          id="input_expirationMonth"
          name="expirationMonth"
          value={formValues.expirationMonth}
        />
        <input
          hidden
          id="input_expirationYear"
          name="expirationYear"
          value={formValues.expirationYear}
        />
        <input
          hidden
          id="input_identificationNumber"
          name="identificationNumber"
          value={formValues.identificationNumber}
        />
        <select hidden id="select_issuer" name="issuer" />
        <select hidden id="select_installments" name="installments" />
      </form>
    </>
  );
}

export default CreditCardDataCard;
